import { useState, useEffect } from 'react';
import axios from 'axios';
import { Save, Leaf, Palette, Axe, Lightbulb, Code, AlertCircle, LayoutGrid, Eye, EyeOff, RefreshCw } from 'lucide-react';
import AdminLayout from '@/components/admin/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';

// Helper to extract iframe src from HTML code
const extractIframeSrc = (html) => {
  if (!html) return null;
  const match = html.match(/src=["']([^"']+)["']/);
  return match ? match[1] : null;
};

// Iframe Preview Component - uses ref to avoid re-renders affecting input focus
const IframePreview = ({ embedHtml, testId }) => {
  const [showPreview, setShowPreview] = useState(false);
  const [key, setKey] = useState(0);
  const iframeSrc = extractIframeSrc(embedHtml);
  
  const refreshPreview = () => {
    setKey(k => k + 1);
  };

  if (!embedHtml) return null;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label>Aperçu</Label>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setShowPreview(!showPreview)}
            className="text-xs"
          >
            {showPreview ? <EyeOff className="w-4 h-4 mr-1" /> : <Eye className="w-4 h-4 mr-1" />}
            {showPreview ? 'Masquer' : 'Afficher'}
          </Button>
          {showPreview && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={refreshPreview}
              className="text-xs"
            >
              <RefreshCw className="w-4 h-4 mr-1" />
              Rafraîchir
            </Button>
          )}
        </div>
      </div>
      
      {showPreview && (
        <div className="border border-stone-200 rounded-xl bg-stone-50 overflow-hidden">
          {iframeSrc ? (
            <iframe
              key={key}
              src={iframeSrc}
              title="Preview"
              className="w-full h-[400px] border-0"
              data-testid={`${testId}-preview-iframe`}
            />
          ) : (
            <div 
              className="p-4 min-h-[200px] max-h-[400px] overflow-auto"
              dangerouslySetInnerHTML={{ __html: embedHtml }}
            />
          )}
        </div>
      )}
      
      {!showPreview && (
        <p className="text-stone-400 text-xs italic">Cliquez sur "Afficher" pour voir l'aperçu de l'iframe</p>
      )}
    </div>
  );
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminPages = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  const [dispatcherData, setDispatcherData] = useState({
    title: 'Widget Réserver',
    description: '',
    embed_html: ''
  });
  const [entretienData, setEntretienData] = useState({
    title: '',
    description: '',
    embed_html: ''
  });
  const [amenagementData, setAmenagementData] = useState({
    title: '',
    description: '',
    embed_html: ''
  });
  const [elagageData, setElagageData] = useState({
    title: '',
    description: '',
    embed_html: ''
  });
  const [conseilData, setConseilData] = useState({
    title: '',
    description: '',
    embed_html: ''
  });

  useEffect(() => {
    fetchPageSettings();
  }, []);

  const fetchPageSettings = async () => {
    try {
      const [dispatcherRes, entretienRes, amenagementRes, elagageRes, conseilRes] = await Promise.all([
        axios.get(`${API}/pages/dispatcher`).catch(() => ({ data: {} })),
        axios.get(`${API}/pages/entretien`).catch(() => ({ data: {} })),
        axios.get(`${API}/pages/amenagement`).catch(() => ({ data: {} })),
        axios.get(`${API}/pages/elagage`).catch(() => ({ data: {} })),
        axios.get(`${API}/pages/conseil`).catch(() => ({ data: {} }))
      ]);
      
      setDispatcherData({
        title: dispatcherRes.data.title || 'Widget Réserver',
        description: dispatcherRes.data.description || '',
        embed_html: dispatcherRes.data.embed_html || ''
      });
      
      setEntretienData({
        title: entretienRes.data.title || 'Entretien de jardin',
        description: entretienRes.data.description || '',
        embed_html: entretienRes.data.embed_html || ''
      });
      
      setAmenagementData({
        title: amenagementRes.data.title || 'Aménagement paysager',
        description: amenagementRes.data.description || '',
        embed_html: amenagementRes.data.embed_html || ''
      });
      
      setElagageData({
        title: elagageRes.data.title || 'Élagage d\'arbres',
        description: elagageRes.data.description || '',
        embed_html: elagageRes.data.embed_html || ''
      });
      
      setConseilData({
        title: conseilRes.data.title || 'Conseils & Études',
        description: conseilRes.data.description || '',
        embed_html: conseilRes.data.embed_html || ''
      });
    } catch (error) {
      console.error('Error fetching page settings:', error);
      toast.error('Erreur lors du chargement des paramètres');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (pageKey, data) => {
    setSaving(true);
    try {
      await axios.put(`${API}/pages/${pageKey}`, data);
      toast.success('Paramètres enregistrés');
    } catch (error) {
      console.error('Error saving page settings:', error);
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const PageForm = ({ icon: Icon, iconColor, title, description, data, setData, pageKey, testIdPrefix }) => {
    // Local state to prevent re-renders from affecting typing
    const [localTitle, setLocalTitle] = useState(data.title);
    const [localDescription, setLocalDescription] = useState(data.description);
    const [localEmbed, setLocalEmbed] = useState(data.embed_html);
    
    // Sync local state when data prop changes (e.g., on fetch)
    useEffect(() => {
      setLocalTitle(data.title);
      setLocalDescription(data.description);
      setLocalEmbed(data.embed_html);
    }, [data.title, data.description, data.embed_html]);
    
    // Update parent state on blur to avoid constant re-renders
    const handleBlur = (field, value) => {
      setData({ ...data, [field]: value });
    };

    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Icon className={`w-5 h-5 ${iconColor}`} />
            {title}
          </CardTitle>
          <CardDescription>{description}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor={`${testIdPrefix}-title`}>Titre de la page</Label>
            <Input
              id={`${testIdPrefix}-title`}
              value={localTitle}
              onChange={(e) => setLocalTitle(e.target.value)}
              onBlur={(e) => handleBlur('title', e.target.value)}
              placeholder="Titre..."
              data-testid={`${testIdPrefix}-title-input`}
              autoComplete="off"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor={`${testIdPrefix}-description`}>Description</Label>
            <Textarea
              id={`${testIdPrefix}-description`}
              value={localDescription}
              onChange={(e) => setLocalDescription(e.target.value)}
              onBlur={(e) => handleBlur('description', e.target.value)}
              placeholder="Description de la page..."
              rows={3}
              data-testid={`${testIdPrefix}-description-input`}
              autoComplete="off"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor={`${testIdPrefix}-embed`} className="flex items-center gap-2">
              <Code className="w-4 h-4" />
              Code HTML du formulaire de réservation
            </Label>
            <Textarea
              id={`${testIdPrefix}-embed`}
              value={localEmbed}
              onChange={(e) => setLocalEmbed(e.target.value)}
              onBlur={(e) => handleBlur('embed_html', e.target.value)}
              placeholder='<iframe src="https://resawork.fr/widget/..." width="100%" height="700" frameborder="0"></iframe>'
              rows={6}
              className="font-mono text-sm"
              data-testid={`${testIdPrefix}-html-input`}
              autoComplete="off"
              spellCheck="false"
            />
            <p className="text-stone-500 text-xs">
              Collez le code HTML/iframe de votre système de réservation ResaWork
            </p>
          </div>
          
          {/* Preview - Now uses dedicated component */}
          <IframePreview embedHtml={localEmbed} testId={testIdPrefix} />

          <Button 
            onClick={() => {
              // Ensure we save the local state
              const dataToSave = {
                title: localTitle,
                description: localDescription,
                embed_html: localEmbed
              };
              setData(dataToSave);
              handleSave(pageKey, dataToSave);
            }}
            disabled={saving}
            className="btn-primary"
            data-testid={`save-${testIdPrefix}-btn`}
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Enregistrement...' : 'Enregistrer'}
          </Button>
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <AdminLayout title="Configuration des Pages">
        <div className="flex justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-emerald-600 border-t-transparent"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Configuration des Pages de Réservation">
      <div className="space-y-6" data-testid="admin-pages">
        {/* Info Banner */}
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
              <div>
                <p className="text-amber-800 font-medium text-sm">Configuration des formulaires de réservation</p>
                <p className="text-amber-700 text-sm mt-1">
                  Collez ici les codes iframe ResaWork pour chaque type de service. 
                  Ces formulaires seront affichés sur les pages correspondantes du site.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs defaultValue="dispatcher" className="space-y-6">
          <TabsList className="grid w-full grid-cols-5 max-w-3xl">
            <TabsTrigger value="dispatcher" className="flex items-center gap-2 text-xs sm:text-sm" data-testid="tab-dispatcher">
              <LayoutGrid className="w-4 h-4" />
              <span className="hidden sm:inline">Dispatcher</span>
            </TabsTrigger>
            <TabsTrigger value="entretien" className="flex items-center gap-2 text-xs sm:text-sm" data-testid="tab-entretien">
              <Leaf className="w-4 h-4" />
              <span className="hidden sm:inline">Entretien</span>
            </TabsTrigger>
            <TabsTrigger value="amenagement" className="flex items-center gap-2 text-xs sm:text-sm" data-testid="tab-amenagement">
              <Palette className="w-4 h-4" />
              <span className="hidden sm:inline">Aménagement</span>
            </TabsTrigger>
            <TabsTrigger value="elagage" className="flex items-center gap-2 text-xs sm:text-sm" data-testid="tab-elagage">
              <Axe className="w-4 h-4" />
              <span className="hidden sm:inline">Élagage</span>
            </TabsTrigger>
            <TabsTrigger value="conseil" className="flex items-center gap-2 text-xs sm:text-sm" data-testid="tab-conseil">
              <Lightbulb className="w-4 h-4" />
              <span className="hidden sm:inline">Conseil</span>
            </TabsTrigger>
          </TabsList>

          {/* Dispatcher Tab - Widget for "Réserver" button */}
          <TabsContent value="dispatcher">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <LayoutGrid className="w-5 h-5 text-emerald-600" />
                  Widget Bouton "Réserver" (non utilisé)
                </CardTitle>
                <CardDescription>
                  <span className="text-amber-600 font-medium">Note :</span> Le bouton "Réserver" utilise maintenant un menu avec 4 choix qui redirigent vers les pages ci-dessous. 
                  Ce widget dispatcher n'est plus affiché directement mais peut être conservé pour référence.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="dispatcher-embed" className="flex items-center gap-2">
                    <Code className="w-4 h-4" />
                    Code HTML du widget dispatcher ResaWork (optionnel)
                  </Label>
                  <Textarea
                    id="dispatcher-embed"
                    value={dispatcherData.embed_html}
                    onChange={(e) => setDispatcherData({ ...dispatcherData, embed_html: e.target.value })}
                    placeholder='<iframe src="https://resawork.fr/widget/dispatcher/..." width="100%" height="600" frameborder="0"></iframe>'
                    rows={6}
                    className="font-mono text-sm"
                    data-testid="dispatcher-html-input"
                    autoComplete="off"
                    spellCheck="false"
                  />
                  <p className="text-stone-500 text-xs">
                    Ce code est conservé pour référence mais n'est pas affiché sur le site actuellement
                  </p>
                </div>
                
                {/* Preview */}
                <IframePreview embedHtml={dispatcherData.embed_html} testId="dispatcher" />

                <Button 
                  onClick={() => handleSave('dispatcher', dispatcherData)}
                  disabled={saving}
                  className="btn-primary"
                  data-testid="save-dispatcher-btn"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? 'Enregistrement...' : 'Enregistrer'}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Entretien Tab */}
          <TabsContent value="entretien">
            <PageForm
              icon={Leaf}
              iconColor="text-emerald-600"
              title="Page Entretien de Jardin"
              description="Services d'entretien régulier avec crédit d'impôt 50%"
              data={entretienData}
              setData={setEntretienData}
              pageKey="entretien"
              testIdPrefix="entretien"
            />
          </TabsContent>

          {/* Aménagement Tab */}
          <TabsContent value="amenagement">
            <PageForm
              icon={Palette}
              iconColor="text-purple-600"
              title="Page Aménagement Paysager"
              description="Services d'aménagement et création de jardins"
              data={amenagementData}
              setData={setAmenagementData}
              pageKey="amenagement"
              testIdPrefix="amenagement"
            />
          </TabsContent>

          {/* Élagage Tab */}
          <TabsContent value="elagage">
            <PageForm
              icon={Axe}
              iconColor="text-amber-600"
              title="Page Élagage"
              description="Services d'élagage et abattage d'arbres"
              data={elagageData}
              setData={setElagageData}
              pageKey="elagage"
              testIdPrefix="elagage"
            />
          </TabsContent>

          {/* Conseil Tab */}
          <TabsContent value="conseil">
            <PageForm
              icon={Lightbulb}
              iconColor="text-blue-600"
              title="Page Conseils & Études"
              description="Services de conseil et études paysagères"
              data={conseilData}
              setData={setConseilData}
              pageKey="conseil"
              testIdPrefix="conseil"
            />
          </TabsContent>
        </Tabs>
      </div>
    </AdminLayout>
  );
};

export default AdminPages;
